import React, { useEffect, useState } from 'react';
import { fetchSalesHistory } from '../infrastructure/productApi';
import { useComercialContext } from '../../comercial/ComercialContext';
import { spanishDateFormatter } from '../../ui/components/SpanishDateFormatter';

/**
 * Historial de las últimas ventas de este artículo al cliente por el que actúa el
 * comercial. Desde el 14-ago sale SOLO de facturas registradas en Business Central:
 * lo facturado es la venta real. Los pedidos del portal siguen apareciendo (también
 * se facturan), marcados como canal Portal; el resto, como Directo.
 *
 * El badge Combinable lo resuelve el api cruzando la factura con el pedido del portal
 * que la originó: la factura de BC no dice qué promoción se aplicó.
 */
export const SalesHistoryComercial = ({ productCode }) => {
    const { actingAsCliente } = useComercialContext();
    const numCliente = actingAsCliente?.numCliente ?? null;
    const [sales, setSales] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!numCliente || !productCode) {
            setSales(null);
            return undefined;
        }
        fetchSalesHistory(productCode, numCliente)
            .then(data => { if (!cancelled) setSales(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setSales([]); });
        return () => { cancelled = true; };
    }, [productCode, numCliente]);

    if (!numCliente || !sales?.length) return null;

    const isPortal = sale => sale.channel !== 'DIRECT';

    return (
        <div className="border rounded p-3 bg-light">
            <small className="fw-bold text-secondary d-block mb-2">
                Últimas ventas a {actingAsCliente.nombreRazonSocial ?? 'este cliente'} (solo visible para comerciales)
            </small>
            <table className="table table-sm table-borderless mb-1">
                <thead>
                    <tr className="text-muted" style={{ fontSize: '0.75rem' }}>
                        <th>Fecha</th>
                        <th>Canal</th>
                        <th>Factura</th>
                        <th className="text-end">Uds</th>
                        <th className="text-end">Precio base</th>
                        <th className="text-end">Descuento</th>
                        <th className="text-end">Precio neto</th>
                    </tr>
                </thead>
                <tbody>
                    {sales.map((sale, i) => (
                        <tr key={`${sale.orderNumber}-${sale.orderDate}-${i}`} style={{ fontSize: '0.8rem' }}>
                            <td>{spanishDateFormatter(sale.orderDate)}</td>
                            <td>
                                {isPortal(sale) ? (
                                    <span className="badge bg-info text-dark" style={{ fontSize: '0.6rem' }}>
                                        Portal
                                    </span>
                                ) : (
                                    <span className="badge bg-secondary" style={{ fontSize: '0.6rem' }}>
                                        Directo
                                    </span>
                                )}
                            </td>
                            <td title="Nº de factura registrada en Business Central">
                                {sale.orderNumber}
                            </td>
                            <td className="text-end">{sale.quantity}</td>
                            <td className="text-end text-muted">
                                {sale.basePrice == null
                                    ? '—'
                                    : sale.discountPercent > 0
                                        ? <s>{sale.basePrice.toFixed(2)}€</s>
                                        : `${sale.basePrice.toFixed(2)}€`}
                            </td>
                            <td className="text-end">
                                {sale.discountPercent > 0 ? `${sale.discountPercent}%` : '—'}
                                {sale.combinable && (
                                    <span
                                        className="badge bg-warning text-dark ms-1"
                                        style={{ fontSize: '0.6rem', verticalAlign: 'middle' }}
                                        title="El pedido que originó esta factura llevaba una oferta combinable"
                                    >
                                        Combinable
                                    </span>
                                )}
                            </td>
                            <td className="text-end fw-semibold">{sale.unitPrice?.toFixed(2)}€</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                Ventas facturadas de todos los canales; un pedido aparece aquí al facturarse
            </div>
        </div>
    );
};
